import { RefObject, useCallback, useMemo } from "react";

import BulkActions from "components/BulkActions";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import { type BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import {
  BulkActionItemID,
  BulkActionResultTabs,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import useAnalytics from "hooks/useAnalytics";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { StackRoleBindingView } from "shared/Stack/Roles/types";

import StackRolesBulkActionsConfirmActionForm from "./ConfirmActionForm";
import StackRolesBulkActionsConfirmationView from "./ConfirmationView";
import StackRolesBulkActionsResultItem from "./ResultItem";
import StackRolesBulkActionsSelectedItemsView from "./SelectedItemsView";
import {
  StackRolesBulkActions as StackRolesBulkActionsEnum,
  StackRolesBulkActionsAnalyticsPayload,
  StackRolesMutationVariables,
} from "./types";
import useStackRolesBulkActions from "./useStackRolesBulkActions";

type StackRolesBulkActionsProps = {
  virtualizedListContainerRef?: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  stackRolesMap: Map<BulkActionItemID, StackRoleBindingView>;
  onBulkResetAll: () => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onFinish: () => Promise<unknown>;
  onHeightChange?: (height: number) => void;
};

const StackRolesBulkActions = ({
  selectedSet,
  stackRolesMap,
  onBulkResetAll,
  onItemDismiss,
  virtualizedListContainerRef,
  onFinish,
  onHeightChange,
}: StackRolesBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const stackRolesBulkActions = useStackRolesBulkActions();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageStack.StackSettingsRoles,
  });

  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: stackRolesBulkActions,
    selectedSet,
    entityItemsMap: stackRolesMap,
  });

  const onItemTrigger = useCallback(
    (
      id: BulkActionItemID,
      mutationVariables: StackRolesMutationVariables
    ): Promise<BulkActionItemTriggerResult> => {
      const roleBinding = stackRolesMap.get(id);
      if (!roleBinding || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(roleBinding).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(roleBinding, mutationVariables);
    },
    [stackRolesMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageStack.StackSettingsRoles,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<StackRoleBindingView>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    stopExecution();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (
      mutationVariables: StackRolesMutationVariables,
      analyticsPayload: StackRolesBulkActionsAnalyticsPayload
    ) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);
        triggerExecution(items, mutationVariables, skippedItems);
        saveEntityMap(stackRolesMap);
        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      stackRolesMap,
      trackSegmentAnalyticsEvent,
      triggerExecution,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const customConfirmationMessage = useMemo(() => {
    return selectedBulkAction?.key === StackRolesBulkActionsEnum.Unassign ? (
      <>Are you sure you want to unassign these roles from the stack?</>
    ) : null;
  }, [selectedBulkAction?.key]);

  const handleContinueWith = useCallback(() => {
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
  }, [resetSelectedAction, resetBulkActionsExecutionQueue]);

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      onContinueWith={handleContinueWith}
      isExecutingBulkActions={!!resultsTabItems[BulkActionResultTabs.Pending].count}
    >
      <BulkActionsFloatingBar
        virtualizedListContainerRef={virtualizedListContainerRef}
        onHeightChange={onHeightChange}
      >
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageStack.StackSettingsRoles}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <StackRolesBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
              customConfirmationMessage={customConfirmationMessage}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageStack.StackSettingsRoles}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <StackRolesBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <StackRolesBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
              customConfirmationMessage={customConfirmationMessage}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {(props) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageStack.StackSettingsRoles}
              />
              <BulkActionsDrawerResultsStepBody {...props} counter={resultsTabItems}>
                {({ currentTab }) =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((roleBinding) => (
                        <StackRolesBulkActionsResultItem
                          key={roleBinding.id}
                          tab={currentTab}
                          roleBinding={roleBinding}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(roleBinding.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>
              <BulkActionsDrawerResultsStepFooter
                {...props}
                bulkActionItemResults={bulkActionItemResults}
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageStack.StackSettingsRoles}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default StackRolesBulkActions;
